/**
 * Role rules the global router guard applies, kept apart from the router so they can be tested
 * without booting it.
 */

export const CREW_ROLES = Object.freeze(['FIELD_STAFF', 'CONTRACTOR']);

/**
 * Whether the user holds any crew role in the current tenant, alongside other roles or not.
 * Use it for who a crew page is for; crew restrictions follow `isOnlyFieldStaff` instead.
 *
 * @param {string[]} roles the user's roles in the current tenant
 * @returns {boolean}
 */
export function holdsCrewRole(roles) {
    return roles.some((role) => CREW_ROLES.includes(role));
}

/**
 * Where opening `/home` takes this user: Home is the crew page, so anyone without a crew role in
 * the tenant goes to Dashboard. Waits for roles first, so a momentarily empty list never rules.
 *
 * @param {{ currentRoles: string[], ensureRolesResolved: () => Promise<boolean> }} userStore
 * @returns {Promise<true|{ name: string }>}
 */
export async function homeRouteTarget(userStore) {
    if (userStore.currentRoles.length === 0) {
        await Promise.race([userStore.ensureRolesResolved(), new Promise((resolve) => setTimeout(resolve, 15000))]);
    }

    return holdsCrewRole(userStore.currentRoles) ? true : { name: 'Dashboard' };
}

/**
 * The only routes a crew-only user may open; anything else sends them Home.
 *
 * @type {readonly string[]}
 */
export const CREW_ROUTES = Object.freeze([
    'Home',
    'SelectTenant',
    'TaskDetail',
    'My account',
    'Account',
    'FieldStaffSchedule',
    'FieldStaffNotifications',
    'FieldStaffRunsheets',
    'FieldStaffRunsheetShow',
    'CompanyDiary',
    'FormsIndex',
    'FormsFill',
    'FormsSubmissionsIndex',
    'FormsSubmissionsView',
    'TimesheetStaff',
    'TimesheetView',
    'WeeklyTimesheet',
    'JobsIndex',
    'OrderView',
    'OrderPreJobChecklist',
    'OrderInventory',
    'OrderInventoryConditionReport',
    'IcrSignOff',
    'IcrViewSignedOff',
    'OrderNotFound',
    'Help',
    '403Page',
    '404Page',
    '503Page',
]);

/**
 * Whether a route's deny list refuses this user.
 *
 * A denied role refuses only when the user holds no role the route admits: every role they hold
 * is either denied or a crew role. So Ops + Sales passes a Sales denial, while Sales + Field Staff
 * does not. A user with no roles in the tenant (a platform user) holds no denied role and passes.
 *
 * @param {string[]} roles the user's roles in the current tenant
 * @param {string[]|null} denyRoles the route's deny list
 * @returns {boolean}
 */
export function isRefusedByDenyRoles(roles, denyRoles) {
    if (!denyRoles?.length || !roles.some((role) => denyRoles.includes(role))) {
        return false;
    }

    return roles.every((role) => denyRoles.includes(role) || CREW_ROLES.includes(role));
}
